import React from 'react';
import PropTypes from 'prop-types';
import styled, { withTheme } from 'styled-components';
import { hook, withValue } from '@g4/automation-hooks';
import moment from '@g4/moment-utc';
import tracking from '@g4/g4-analytics';

import useClientWidth from '../../../hooks/useClientWidth';
import {
  tripTypeValues,
  propTypes as flightSearchCriteriaPropTypes,
} from '../../../hooks/useFlightSearchCriteria';
import { Box, Expando, VerticalSpacing, Text, useMedia } from '../../Boneyard';
import WrappedFlightSearchForm from './flightSummary/WrappedFlightSearchForm';
import { useTranslations } from '../../../graphql/components/TranslationsProvider';

import CLOSE_ICON from '../../../images/ic-close-white.svg';

import {
  HEADER_FLIGHT_INFO,
  HEADER_SEARCH_WIDGET,
} from '../../../../automationHooks.json';

const propTypes = {
  isFormOpen: PropTypes.bool,
  onFormToggle: PropTypes.func.isRequired,
  flightSearchCriteria: flightSearchCriteriaPropTypes,
};

const FlightSummaryWrapper = styled.div`
  flex-grow: 1;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  width: 100%;
  border-top: 1px solid white;
  padding: ${props => props.theme.spacing(2)} 0
    ${props => props.theme.spacing(3)};
  order: 3;
  ${props => props.theme.query.lg} {
    width: auto;
    border-top: none;
    padding: 0;
    order: 1;
    margin-left: ${props =>
      props.smallMargin ? props.theme.spacing(4) : props.theme.spacing(20)};
  }
`;

const AirportCodes = styled.div`
  font-size: ${props => props.theme.toRem(20)};
  font-weight: 600;
  display: flex;
  align-items: baseline;
  margin-right: ${props => props.theme.spacing(2)};
  & > *:nth-child(2) {
    margin-left: 3px;
    margin-right: 3px;
  }
  width: 100%;
  ${props => props.theme.query.sm} {
    width: auto;
  }
  ${props => props.theme.query.md} {
    font-size: ${props => props.theme.toRem(20)};
    margin-right: ${props => props.theme.spacing(3)};
    & > *:nth-child(2) {
      margin-left: 5px;
      margin-right: 5px;
    }
  }
`;

const FlightParameter = styled.div`
  color: white;
  flex-shrink: 0;
  margin-top: ${props => props.theme.spacing(1)};
  margin-bottom: ${props => props.theme.spacing(1)};
  margin-right: ${props => props.theme.spacing(2)};
  font-size: 16px;
  font-weight: 500;
  display: block;
  ${props =>
    props.showSeparator &&
    `
      &::after {
        content: '|';
        font-weight: 600;
        margin-left: ${props.theme.spacing(2)};
      }
    `}
  ${props => props.theme.query.md} {
    margin-right: ${props => props.theme.spacing(3)};
    font-size: 16px;
    ${props =>
      props.showSeparator &&
      `&::after {
      margin-left: ${props.theme.spacing(3)};
      }
    `}
  }
`;

const SearchFormWrapper = styled.div`
  position: absolute;
  left: 50%;
  top: 120px;
  /** Avoid 1px gap at edge of window */
  width: ${props => props.containerWidth + 1}px;
  transform: translateX(-50%);
  background-image: linear-gradient(
    ${props => props.theme.colors.siteHeader.background.from},
    ${props => props.theme.colors.siteHeader.background.to}
  );

  ${props => props.theme.query.sm} {
    top: 100px;
  }

  ${props => props.theme.query.md} {
    top: 125px;
  }

  ${props => props.theme.query.lg} {
    top: 70px;
  }
`;

const CloseFormButton = styled.button`
  position: absolute;
  top: 0;
  z-index: 1;
  right: 12px;
  color: ${props => props.theme.colors.common.light};
`;

const Constrain = styled.div`
  position: relative;
  max-width: 1200px;
  width: 100%;
  margin-left: auto;
  margin-right: auto;
  padding: 0 ${props => props.theme.spacing(3)};
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  ${props => props.theme.query.lg} {
    flex-wrap: nowrap;
  }
`;

const GAClickEventTracker = styled.div``;

const ArrowIcon = withTheme(({ theme, isItemOpen, isSmall }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 20 20"
    style={{
      transform: isItemOpen ? 'rotate(-180deg)' : 'rotate(0deg)',
      marginLeft: '3px',
      flexShrink: '0',
      width: isSmall ? '15px' : '20px',
      height: isSmall ? '15px' : '20px',
    }}
  >
    <polygon fill={theme.colors.siteHeader.text} points="10,15 4,7 16,7 " />
  </svg>
));

ArrowIcon.propTypes = {
  isItemOpen: PropTypes.bool,
  isSmall: PropTypes.bool,
};

const FlightSummary = ({ isFormOpen, onFormToggle, flightSearchCriteria }) => {
  const mq = useMedia();
  const clientWidth = useClientWidth();
  const translations = useTranslations();

  const {
    toLabel,
    roundTripLabel,
    oneWayLabel,
    seatedDescription,
    inLapDescription,
    searchExpandButtonLabel,
    searchExpandButtonA11yLabel,
  } = translations.flightSearchForm;

  const {
    tripType,
    origin,
    destination,
    departDate,
    returnDate,
    adultsCount,
    childrenCount,
    lapInfantCount,
    infantCount,
  } = flightSearchCriteria;

  const travelersInSeat = adultsCount + childrenCount + infantCount;
  const travelersInLap = lapInfantCount;
  const route = origin + destination;

  const isRoundTrip = tripType === tripTypeValues.ROUNDTRIP;

  const handleKeyPress = event => {
    if (event.key === 'Enter') {
      onFormToggle(event);
    }
  };

  return (
    <FlightSummaryWrapper smallMargin={isRoundTrip || Boolean(travelersInLap)}>
      <AirportCodes>
        <span {...hook(HEADER_FLIGHT_INFO.hook, 'origin')}>{origin}</span>
        <Text weight={500} size={mq.gte('md') ? 16 : 12}>
          {toLabel}
        </Text>
        <span {...hook(HEADER_FLIGHT_INFO.hook, 'destination')}>
          {destination}
        </span>
      </AirportCodes>
      <Box d="flex" alignItems="center">
        <FlightParameter
          showSeparator
          {...hook(HEADER_FLIGHT_INFO.hook, 'trip-type')}
        >
          {isRoundTrip ? roundTripLabel : oneWayLabel}
        </FlightParameter>
        <FlightParameter showSeparator>
          <span
            {...hook(
              HEADER_FLIGHT_INFO.hook,
              'departing-date_' +
                moment(departDate.date, 'YYYY-MM-DD').format('YYYY-MM-DD')
            )}
          >
            {moment(departDate.date, 'YYYY-MM-DD').format('MMM DD')}
          </span>
          {isRoundTrip && (
            <>
              {' - '}
              <span
                {...hook(
                  HEADER_FLIGHT_INFO.hook,
                  'return-date_' +
                    moment(returnDate?.date, 'YYYY-MM-DD').format('YYYY-MM-DD')
                )}
              >
                {moment(returnDate?.date, 'YYYY-MM-DD').format('MMM DD')}
              </span>
            </>
          )}
        </FlightParameter>
        <FlightParameter showSeparator>
          <span {...hook(HEADER_FLIGHT_INFO.hook, 'seated')}>
            {travelersInSeat} {seatedDescription}
          </span>
          {Boolean(travelersInLap) && (
            <>
              {', '}
              <span {...hook(HEADER_FLIGHT_INFO.hook, 'in-lap')}>
                {travelersInLap} {inLapDescription}
              </span>
            </>
          )}
        </FlightParameter>
        <GAClickEventTracker
          onClick={() => {
            if (!isFormOpen) {
              tracking.trackModifySearch(route);
            }
          }}
          tabIndex={0}
          onKeyPress={handleKeyPress}
        >
          <Expando
            label={
              <Text
                size={mq.gte('md') ? 16 : 12}
                automationHook={withValue(HEADER_SEARCH_WIDGET.hook, 'trigger')}
              >
                {searchExpandButtonLabel}
              </Text>
            }
            open={isFormOpen}
            onToggle={onFormToggle}
            icon={
              <ArrowIcon
                isItemOpen={isFormOpen}
                isSmall={mq.lt('md') ? true : false}
              />
            }
            a11yLabel={searchExpandButtonA11yLabel}
          >
            <WrappedFlightSearchForm searchResultsPath="/booking/flights">
              {renderForm =>
                isFormOpen && (
                  <SearchFormWrapper
                    containerWidth={clientWidth}
                    {...hook(HEADER_SEARCH_WIDGET.hook)}
                  >
                    <VerticalSpacing size={10} />
                    <Constrain>
                      <CloseFormButton onClick={onFormToggle} type="button">
                        <img src={CLOSE_ICON} alt="Close" />
                      </CloseFormButton>
                      {renderForm({ onSubmit: onFormToggle })}
                    </Constrain>
                    <VerticalSpacing size={14} />
                  </SearchFormWrapper>
                )
              }
            </WrappedFlightSearchForm>
          </Expando>
        </GAClickEventTracker>
      </Box>
    </FlightSummaryWrapper>
  );
};

FlightSummary.propTypes = propTypes;

export default FlightSummary;
